import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  collection, query, where, getDocs, getDoc, doc, updateDoc, Timestamp, orderBy,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { receivableStatusLabels } from '../types';

/**
 * 未収金（ツケ）台帳。
 *
 * オーナー / 責任者の PC 操作を前提にした画面。
 * 入金の記録と貸倒処理はここだけで行い、アプリ側からは触らせない
 * （firestore.rules でも update をオーナー/マネージャーに限っている）。
 */

interface ReceivableRow {
  id: string;
  customerName: string;
  phoneNumber?: string;
  businessDate: string;
  amount: number;
  paidAmount: number;
  balance: number;
  responsibleStaffName?: string;
  dueDate?: Date;
  status: 'open' | 'partially_paid' | 'paid' | 'written_off';
  checkoutId: string;
  note?: string;
}

const statusColor: Record<ReceivableRow['status'], 'warning' | 'info' | 'success' | 'error'> = {
  open: 'warning',
  partially_paid: 'info',
  paid: 'success',
  written_off: 'error',
};

const Receivables: React.FC = () => {
  const { shop, user } = useAuth();
  const [rows, setRows] = useState<ReceivableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'unpaid' | 'all'>('unpaid');

  const [payDialog, setPayDialog] = useState<{ open: boolean; row: ReceivableRow | null }>({ open: false, row: null });
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<'cash' | 'credit_card' | 'transfer' | 'other'>('cash');
  const [payNote, setPayNote] = useState('');

  const [writeOffDialog, setWriteOffDialog] = useState<{ open: boolean; row: ReceivableRow | null }>({ open: false, row: null });
  const [writeOffReason, setWriteOffReason] = useState('');

  const load = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(
        collection(db, 'receivables'),
        where('shopId', '==', shop.id),
        orderBy('createdAt', 'desc'),
      ));
      setRows(snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          customerName: data.customerName ?? '',
          phoneNumber: data.phoneNumber ?? undefined,
          businessDate: data.businessDate ?? '',
          amount: data.amount ?? 0,
          paidAmount: data.paidAmount ?? 0,
          balance: data.balance ?? 0,
          responsibleStaffName: data.responsibleStaffName ?? undefined,
          dueDate: data.dueDate?.toDate?.(),
          status: data.status ?? 'open',
          checkoutId: data.checkoutId ?? '',
          note: data.note ?? undefined,
        };
      }));
    } catch (e: any) {
      setError(e?.message ?? '未収金の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shop]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.balance > 0 && r.status !== 'written_off')),
    [rows, filter]
  );

  const totals = useMemo(() => {
    const open = rows.filter((r) => r.status !== 'written_off' && r.balance > 0);
    const overdue = open.filter((r) => r.dueDate && r.dueDate.getTime() < Date.now());
    return {
      outstanding: open.reduce((s, r) => s + r.balance, 0),
      count: open.length,
      overdueAmount: overdue.reduce((s, r) => s + r.balance, 0),
      overdueCount: overdue.length,
      writtenOff: rows.filter((r) => r.status === 'written_off').reduce((s, r) => s + r.balance, 0),
    };
  }, [rows]);

  const submitPayment = async () => {
    const row = payDialog.row;
    if (!row) return;
    const amount = Math.max(0, Number(payAmount));
    if (amount <= 0) return;
    const applied = Math.min(amount, row.balance);
    const newPaid = row.paidAmount + applied;
    const newBalance = row.amount - newPaid;
    try {
      await updateDoc(doc(db, 'receivables', row.id), {
        paidAmount: newPaid,
        balance: newBalance,
        status: newBalance <= 0 ? 'paid' : 'partially_paid',
        payments: [
          ...(await getPayments(row.id)),
          {
            paidAt: Timestamp.now(),
            amount: applied,
            method: payMethod,
            receivedBy: user?.uid ?? '',
            receivedByName: user?.email ?? '',
            note: payNote || null,
          },
        ],
        updatedAt: Timestamp.now(),
      });
      setPayDialog({ open: false, row: null });
      setPayAmount('');
      setPayNote('');
      await load();
    } catch (e: any) {
      setError(e?.message ?? '入金の記録に失敗しました');
    }
  };

  /** 既存の入金履歴を読む（配列を丸ごと置き換えるため、追記前に現物が要る） */
  const getPayments = async (id: string): Promise<any[]> => {
    const snap = await getDoc(doc(db, 'receivables', id));
    return ((snap.data() as any)?.payments ?? []) as any[];
  };

  const submitWriteOff = async () => {
    const row = writeOffDialog.row;
    if (!row || !writeOffReason.trim()) return;
    try {
      await updateDoc(doc(db, 'receivables', row.id), {
        status: 'written_off',
        writtenOffAt: Timestamp.now(),
        writtenOffBy: user?.uid ?? '',
        writtenOffReason: writeOffReason.trim(),
        updatedAt: Timestamp.now(),
      });
      setWriteOffDialog({ open: false, row: null });
      setWriteOffReason('');
      await load();
    } catch (e: any) {
      setError(e?.message ?? '貸倒処理に失敗しました');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">未収金（ツケ）台帳</Typography>
        <TextField
          select size="small" value={filter}
          onChange={(e) => setFilter(e.target.value as 'unpaid' | 'all')}
          sx={{ width: 160 }}
        >
          <MenuItem value="unpaid">未回収のみ</MenuItem>
          <MenuItem value="all">すべて</MenuItem>
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">未回収残高</Typography>
          <Typography variant="h5" fontWeight="bold">¥{totals.outstanding.toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">{totals.count} 件</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, borderLeft: totals.overdueCount > 0 ? '4px solid' : undefined, borderColor: 'error.main' }}>
          <Typography variant="caption" color="text.secondary">期日超過</Typography>
          <Typography variant="h5" fontWeight="bold" color={totals.overdueCount > 0 ? 'error.main' : undefined}>
            ¥{totals.overdueAmount.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">{totals.overdueCount} 件</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">貸倒処理済み</Typography>
          <Typography variant="h5" fontWeight="bold">¥{totals.writtenOff.toLocaleString()}</Typography>
        </Paper>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Alert severity="success">未回収のツケはありません。</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>営業日</TableCell>
                <TableCell>お客様</TableCell>
                <TableCell>回収責任者</TableCell>
                <TableCell align="right">発生額</TableCell>
                <TableCell align="right">入金</TableCell>
                <TableCell align="right">残高</TableCell>
                <TableCell>期日</TableCell>
                <TableCell>状態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const overdue = r.dueDate && r.dueDate.getTime() < Date.now() && r.balance > 0;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.businessDate}</TableCell>
                    <TableCell>
                      {r.customerName}
                      {r.phoneNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">{r.phoneNumber}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{r.responsibleStaffName ?? '—'}</TableCell>
                    <TableCell align="right">¥{r.amount.toLocaleString()}</TableCell>
                    <TableCell align="right">¥{r.paidAmount.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>¥{r.balance.toLocaleString()}</TableCell>
                    <TableCell>
                      {r.dueDate ? (
                        <Typography variant="body2" color={overdue ? 'error.main' : undefined}>
                          {r.dueDate.toLocaleDateString('ja-JP')}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={receivableStatusLabels[r.status]} color={statusColor[r.status]} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small" variant="outlined"
                          disabled={r.balance <= 0 || r.status === 'written_off'}
                          onClick={() => { setPayDialog({ open: true, row: r }); setPayAmount(String(r.balance)); }}
                        >
                          入金
                        </Button>
                        <Button
                          size="small" color="error"
                          disabled={r.balance <= 0 || r.status === 'written_off'}
                          onClick={() => setWriteOffDialog({ open: true, row: r })}
                        >
                          貸倒
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={payDialog.open} onClose={() => setPayDialog({ open: false, row: null })} fullWidth maxWidth="xs">
        <DialogTitle>入金を記録</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {payDialog.row?.customerName} / 残高 ¥{payDialog.row?.balance.toLocaleString()}
          </Typography>
          <TextField
            label="入金額" type="number" fullWidth sx={{ mb: 2 }}
            value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
          />
          <TextField
            select label="方法" fullWidth sx={{ mb: 2 }}
            value={payMethod} onChange={(e) => setPayMethod(e.target.value as any)}
          >
            <MenuItem value="cash">現金</MenuItem>
            <MenuItem value="credit_card">カード</MenuItem>
            <MenuItem value="transfer">振込</MenuItem>
            <MenuItem value="other">その他</MenuItem>
          </TextField>
          <TextField label="メモ" fullWidth value={payNote} onChange={(e) => setPayNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog({ open: false, row: null })}>キャンセル</Button>
          <Button variant="contained" onClick={submitPayment}>記録する</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={writeOffDialog.open} onClose={() => setWriteOffDialog({ open: false, row: null })} fullWidth maxWidth="xs">
        <DialogTitle>貸倒として処理</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            残高 ¥{writeOffDialog.row?.balance.toLocaleString()} を回収不能として計上します。記録は消えません。
          </Alert>
          <TextField
            label="理由（必須）" fullWidth multiline rows={3}
            value={writeOffReason} onChange={(e) => setWriteOffReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteOffDialog({ open: false, row: null })}>キャンセル</Button>
          <Button variant="contained" color="error" disabled={!writeOffReason.trim()} onClick={submitWriteOff}>
            貸倒にする
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Receivables;
